import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Button, 
  Card, 
  Chip,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  ClickAwayListener,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Snackbar,
  Stack,
  Divider,
  Grid,
} from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import { ENABLE_TAB_CONTRACT, ENABLE_TAB_MAPPING } from "../config/local.config";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import HelpIcon from '@mui/icons-material/Help';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ProjectUsers from '../components/ProjectUsers';
import ProjectTeams from '../components/ProjectTeams';
import { UserContext } from '../components/MiniDrawer';

// Dynamic tab index calculator — keeps indices stable regardless of which tabs are enabled.
// Fixed tabs: 0=General, 1=Users, 2=Teams, 3=Activities
const TAB_INDEX = (() => {
  let next = 4;
  return {
    CONTRACT: ENABLE_TAB_CONTRACT ? next++ : -1,
    MAPPING:  ENABLE_TAB_MAPPING  ? next++ : -1,
  };
})();

function ProjectDetailTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            bgcolor: 'background.paper',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

/** Corporate / logo green — used for primary actions and selected project tabs. */
const RUN_BRAND_GREEN = '#388e3c';
const RUN_BRAND_GREEN_HOVER = '#2e7d32';

/** Shared styles for Project detail horizontal tabs (pill selected state, no underline indicator). */
const PROJECT_DETAIL_TAB_SX = {
  minHeight: 44,
  py: 1,
  px: { xs: 1.25, sm: 2 },
  mx: 0.35,
  my: 1,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'text.secondary',
  transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  '&.Mui-selected': {
    color: '#fff',
    bgcolor: RUN_BRAND_GREEN,
    boxShadow: 1,
    '&:hover': {
      bgcolor: RUN_BRAND_GREEN_HOVER,
      color: '#fff',
    },
    '& .MuiTab-iconWrapper': {
      color: 'inherit',
    },
  },
  '&:not(.Mui-selected):hover': {
    bgcolor: 'action.hover',
    color: 'text.primary',
  },
  '& .MuiTab-iconWrapper': {
    marginBottom: '0 !important',
    marginRight: { xs: 0, sm: 0.5 },
  },
  '& .MuiTab-iconWrapper + span': {
    display: { xs: 'none', sm: 'inline' },
    marginLeft: { sm: 0.5 },
  },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { backendUser } = useContext(UserContext);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  // States per il dialog di modifica
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
    start_date: '',
    end_date: '',
    active: true,
    project_code: '',
    days_after_block: 1,
    days_before_block: 1,
    ticket_id_prefix: '',
    regex: '',
    regex_desc: '',
    previous_month_blocked: false,
    yubiq_code: '',
    tipologia_servizio: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [yubiqOptions, setYubiqOptions] = useState([]);
  const [regexHelpOpen, setRegexHelpOpen] = useState(false);

  // Activities tab state
  const [activities, setActivities] = useState([]);
  const [projectActivityIds, setProjectActivityIds] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesSaving, setActivitiesSaving] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);

  // Mapping tab state (nuovo modello RUNR)
  const [selectedDataSource, setSelectedDataSource] = useState('');
  // savedDataSourceType: ciò che è effettivamente persisito in DB ('' = non salvato).
  const [savedDataSourceType, setSavedDataSourceType] = useState('');
  const [isMasterServiceDraft, setIsMasterServiceDraft] = useState(false);
  const [savedIsMasterService, setSavedIsMasterService] = useState(false);
  const [jiraProjectIdDraft, setJiraProjectIdDraft] = useState('');
  const isEditingJiraProjectIdRef = useRef(false);
  const jiraProjectIdDirtyRef = useRef(false);
  const mappingFormInitializedRef = useRef(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingError, setMappingError] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingDialogType, setMappingDialogType] = useState('field');
  const [editingSheetMappingId, setEditingSheetMappingId] = useState(null);
  const [mappingDialogError, setMappingDialogError] = useState(null);
  const [mappingDialogForm, setMappingDialogForm] = useState({
    file_url: '',
    file_name: '',
    ticketing_system: '',
    data_structure: '',
  });
  const [dataStructureOptions, setDataStructureOptions] = useState([
    { value: 'column', label: 'Column' },
    { value: 'row', label: 'Row' },
  ]);
  const [mappingItems, setMappingItems] = useState([]);
  const [ticketingOptions, setTicketingOptions] = useState([
    { value: 'servicenow', label: 'ServiceNow' },
    { value: 'remedy', label: 'Remedy' },
    { value: 'jira', label: 'Jira' },
    { value: 'other', label: 'Other' },
  ]);
  const [mappingLogicOptions, setMappingLogicOptions] = useState([
    { value: 'move', label: 'Move' },
    { value: 'mapping', label: 'Mapping' },
    { value: 'choice', label: 'Choice' },
    { value: 'constant', label: 'Constant' },
    { value: 'format', label: 'Format' },
    { value: 'dimension', label: 'Dimension' },
  ]);
  const [configDialogLoading, setConfigDialogLoading] = useState(false);
  const [configDialogSavingAll, setConfigDialogSavingAll] = useState(false);
  const [configDialogError, setConfigDialogError] = useState(null);
  const [selectedSheetMapping, setSelectedSheetMapping] = useState(null);
  const [configRows, setConfigRows] = useState([]);
  const [configInitialSnapshot, setConfigInitialSnapshot] = useState('');
  const [configSearchTerm, setConfigSearchTerm] = useState('');
  const [configFilter, setConfigFilter] = useState('all');
  const [mappingToast, setMappingToast] = useState({ open: false, message: '', severity: 'success' });
  const [mappingValuesDialogOpen, setMappingValuesDialogOpen] = useState(false);
  const [mappingValuesLoading, setMappingValuesLoading] = useState(false);
  const [mappingValuesError, setMappingValuesError] = useState(null);
  const [mappingValues, setMappingValues] = useState([]);
  const [missingMappingAlertByField, setMissingMappingAlertByField] = useState({});
  // field_key del campo con help aperto al click (resta aperto finche non si clicca fuori).
  const [pinnedHelpFieldKey, setPinnedHelpFieldKey] = useState(null);
  const [hoveredHelpFieldKey, setHoveredHelpFieldKey] = useState(null);
  const [selectedConfigRowForValues, setSelectedConfigRowForValues] = useState(null);
  const [normalizedOptionsForValues, setNormalizedOptionsForValues] = useState([]);
  const [mappingValueForm, setMappingValueForm] = useState({
    id: null,
    source_value: '',
    normalized_ref_id: '',
  });
  const [formatHelpOpen, setFormatHelpOpen] = useState(false);
  // Opzioni lookup per dominio di ref_lookup_values (choice: lookup_domain || field_key,
  // dimension: lookup_domain || 'dimension').
  const [lookupOptionsByDomain, setLookupOptionsByDomain] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const [confirmDialogBusy, setConfirmDialogBusy] = useState(false);

  // Contract tab state
  const [contractData, setContractData] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractSaving, setContractSaving] = useState(false);
  const [contractError, setContractError] = useState(null);
  const [contractEditMode, setContractEditMode] = useState(false);
  const [contractForm, setContractForm] = useState({
    tipologia_servizio: '',
    ricavi_actual: '',
    ricavi_anno_prec: '',
    ricavi_anno_corrente: '',
    ricavi_anno_succ: '',
    canone_mensile: '',
  });
  const [contractToast, setContractToast] = useState({ open: false, message: '', severity: 'success' });
  const [typeOptions, setTypeOptions] = useState([]);

  const fetchTypeOptions = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=contract_type`);
      if (!response.ok) return;
      const data = await response.json();
      setTypeOptions(data.items || []);
    } catch {
      setTypeOptions([]);
    }
  }, []);

  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[PROJECT] Fetching project:', id);
      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${id}`);
      console.log('[PROJECT] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('[PROJECT] Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('[PROJECT] Response data:', data);
      const projectData = data.project || data;
      console.log('[PROJECT] Project loaded:', projectData);
      setProject(projectData);
    } catch (err) {
      console.error('[PROJECT] Fetch error:', err);
      setError(`Error loading project: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleBack = () => {
    navigate('/projects');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatSimpleDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.CUSTOMERS);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || data || []);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const handleEditDialog = () => {
    if (!project) return;
    
    // Debug: log delle date ricevute dal backend
    console.log('[PROJECT EDIT] Date from backend:', {
      start_date: project.start_date,
      end_date: project.end_date
    });
    
    const getFormattedDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        // Se la data è già in formato YYYY-MM-DD, restituiscila direttamente
        if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // Se la data contiene 'T' (formato ISO con orario), estrai solo la parte data
        if (typeof dateStr === 'string' && dateStr.includes('T')) {
          return dateStr.split('T')[0];
        }
        // Fallback: converti tramite Date object
        const date = new Date(dateStr);
        // Usa le componenti locali per evitare shift di timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        return '';
      }
    };

    const formattedStartDate = getFormattedDate(project.start_date);
    const formattedEndDate = getFormattedDate(project.end_date);
    
    // Debug: log delle date formattate per il form
    console.log('[PROJECT EDIT] Formatted dates for form:', {
      start_date: formattedStartDate,
      end_date: formattedEndDate
    });

    setFormData({
      name: project.name || '',
      description: project.description || '',
      customer_id: project.customer_id || '',
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      active: project.active !== false,
      project_code: project.project_code || '',
      days_after_block: project.days_after_block || 1,
      days_before_block: project.days_before_block || 1,
      ticket_id_prefix: project.ticket_id_prefix || '',
      regex: project.regex || '',
      regex_desc: project.regex_desc || '',
      previous_month_blocked: project.previous_month_blocked || false,
      yubiq_code: project.yubiq_code || '',
      tipologia_servizio: project.tipologia_servizio || '',
    });
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Cambiando customer, resetta lo Yubiq Code (appartiene ad un altro customer)
      ...(name === 'customer_id' ? { yubiq_code: '' } : {}),
    }));
  };

  // Carica gli Yubiq Code disponibili (dai Contracts) per il customer selezionato nel dialog
  useEffect(() => {
    if (!openDialog || !formData.customer_id) {
      setYubiqOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.CONTRACTS}?customer_id=${formData.customer_id}`);
        if (!res.ok) return;
        const data = await res.json();
        const codes = (data.contracts || [])
          .map((c) => c.yubiq_code)
          .filter((code) => code != null && code !== '');
        if (!cancelled) setYubiqOptions(Array.from(new Set(codes)));
      } catch {
        if (!cancelled) setYubiqOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [openDialog, formData.customer_id]);

  const handleRegexHelpOpen = () => {
    setRegexHelpOpen(true);
  };

  const handleRegexHelpClose = () => {
    setRegexHelpOpen(false);
  };

  const handleDataSourceChange = (event) => {
    const newValue = event.target.value;
    setSelectedDataSource(newValue);
    mappingFormInitializedRef.current = true;
    setMappingError(null);
    if (newValue !== 'jira') {
      setJiraProjectIdDraft('');
      jiraProjectIdDirtyRef.current = false;
    }
  };

  const handleMasterServiceChange = (event) => {
    setIsMasterServiceDraft(event.target.checked);
    mappingFormInitializedRef.current = true;
    setMappingError(null);
  };

  const openAddMappingDialog = (type) => {
    setMappingDialogType(type);
    setEditingSheetMappingId(null);
    setMappingDialogForm({ file_url: '', file_name: '', ticketing_system: '', data_structure: '' });
    setMappingDialogError(null);
    setMappingDialogOpen(true);
  };

  const handleAddFieldMapping = async () => {
    if (!savedIsMasterService) {
      setMappingError('Master service must be enabled to add sheet mappings.');
      return;
    }
    // Auto-save data source if it hasn't been persisted yet.
    if (savedDataSourceType !== 'gsheet') {
      const ok = await handleSaveDataSource();
      if (!ok) return; // Error already set by handleSaveDataSource.
    }
    openAddMappingDialog('field');
  };

  const handleAddSlaMapping = async () => {
    if (!savedIsMasterService) {
      setMappingError('Master service must be enabled to add sheet mappings.');
      return;
    }
    // Auto-save data source if it hasn't been persisted yet.
    if (savedDataSourceType !== 'gsheet') {
      const ok = await handleSaveDataSource();
      if (!ok) return; // Error already set by handleSaveDataSource.
    }
    openAddMappingDialog('sla');
  };

  const handleCloseMappingDialog = () => {
    setMappingDialogOpen(false);
    setEditingSheetMappingId(null);
    setMappingDialogError(null);
  };

  const handleMappingDialogChange = (e) => {
    const { name, value } = e.target;
    setMappingDialogForm((prev) => ({ ...prev, [name]: value }));
  };

  const parseListPayload = (data, keys = []) => {
    for (const key of keys) {
      if (Array.isArray(data?.[key])) return data[key];
    }
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchMappingData = useCallback(async () => {
    if (!id) return;
    setMappingLoading(true);
    setMappingError(null);
    try {
      const [dataSourceRes, sheetMappingRes, ticketingRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.DATA_SOURCE}?project_id=${id}`),
        fetch(`${API_ENDPOINTS.SHEET_MAPPING}?project_id=${id}`),
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=ticketing_system`),
      ]);

      if (!dataSourceRes.ok) {
        throw new Error(`Unable to load data source (${dataSourceRes.status})`);
      }
      if (!sheetMappingRes.ok) {
        throw new Error(`Unable to load sheet mappings (${sheetMappingRes.status})`);
      }

      const dsPayload = await dataSourceRes.json();
      const sheetPayload = await sheetMappingRes.json();

      const dataSourceCandidates = parseListPayload(dsPayload, ['items', 'data_sources', 'data']);
      const activeSource = dataSourceCandidates.find((row) => row?.is_active !== false) || dataSourceCandidates[0] || dsPayload?.item || dsPayload?.data_source || null;
      const dataSourceType = activeSource?.data_source_type?.toLowerCase() || '';
      const masterService = !!activeSource?.is_master_service;
      setSavedDataSourceType(dataSourceType);
      setSavedIsMasterService(masterService);
      if (!mappingFormInitializedRef.current) {
        if (dataSourceType) {
          setSelectedDataSource(dataSourceType);
          setJiraProjectIdDraft(activeSource?.jira_project_id || '');
          setIsMasterServiceDraft(masterService);
        } else {
          setSelectedDataSource('');
          setJiraProjectIdDraft('');
          setIsMasterServiceDraft(false);
        }
        mappingFormInitializedRef.current = true;
      } else {
        setIsMasterServiceDraft(masterService);
      }

      if (!masterService) {
        resetConfigPanel();
        setMappingValuesDialogOpen(false);
        setSelectedConfigRowForValues(null);
        setMappingValues([]);
        setMappingValuesError(null);
      }

      const rawMappings = parseListPayload(sheetPayload, ['items', 'sheet_mappings', 'data']);
      const mappedItems = rawMappings.map((item) => ({
        id: item.id,
        mapping_type: item.mapping_type,
        file_url: item.file_url,
        file_name: item.file_name,
        ticketing_system: item.ticketing_system,
        is_draft: item.is_draft,
        data_structure: item.data_structure,
      }));
      setMappingItems(mappedItems);

      setSelectedSheetMapping((prev) => {
        if (!prev) return prev;
        const stillExists = mappedItems.some((item) => item.id === prev.id);
        if (stillExists) return prev;

        // If selected mapping was deleted, close configuration panel and related dialogs/state.
        setConfigRows([]);
        setConfigDialogError(null);
        setConfigInitialSnapshot('');
        setConfigSearchTerm('');
        setConfigFilter('all');
        setMappingValuesDialogOpen(false);
        setSelectedConfigRowForValues(null);
        setMappingValues([]);
        setNormalizedOptionsForValues([]);
        setMappingValuesError(null);
        return null;
      });

      if (ticketingRes.ok) {
        const lookupPayload = await ticketingRes.json();
        const lookupItems = parseListPayload(lookupPayload, ['items', 'values', 'data']);
        if (lookupItems.length > 0) {
          setTicketingOptions(
            lookupItems.map((row) => ({
              value: row.value,
              label: row.label || row.value,
            }))
          );
        }
      }

      try {
        const mappingLogicRes = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=mapping_logic`);
        if (mappingLogicRes.ok) {
          const mappingLogicPayload = await mappingLogicRes.json();
          const mappingLogicItems = parseListPayload(mappingLogicPayload, ['items', 'values', 'data']);
          if (mappingLogicItems.length > 0) {
            setMappingLogicOptions(
              mappingLogicItems.map((row) => ({
                value: row.value,
                label: row.label || row.value,
              }))
            );
          }
        }
      } catch {
        // fallback to default static options
      }

      try {
        const dataStructureRes = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=data_structure`);
        if (dataStructureRes.ok) {
          const dataStructurePayload = await dataStructureRes.json();
          const dataStructureItems = parseListPayload(dataStructurePayload, ['items', 'values', 'data']);
          if (dataStructureItems.length > 0) {
            setDataStructureOptions(
              dataStructureItems.map((row) => ({
                value: row.value,
                label: row.label || row.value,
              }))
            );
          }
        }
      } catch {
        // fallback to default static options
      }
    } catch (e) {
      setMappingError(e.message || 'Unable to load mapping configuration.');
    } finally {
      setMappingLoading(false);
    }
  }, [id]);

  const handleSaveDataSource = async () => {
    if (!id) return false;
    if (!selectedDataSource) {
      setMappingError('Please select a Data Source.');
      return false;
    }
    if (isMasterServiceDraft && selectedDataSource === 'jira' && !jiraProjectIdDraft.trim()) {
      setMappingError('Jira Project ID is required when Data Source is JIRA and Master service is enabled.');
      return false;
    }

    setMappingSaving(true);
    setMappingError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DATA_SOURCE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: parseInt(id, 10),
          data_source_type: selectedDataSource,
          is_master_service: isMasterServiceDraft,
          jira_project_id: isMasterServiceDraft && selectedDataSource === 'jira'
            ? jiraProjectIdDraft.trim()
            : null,
          is_active: true,
        }),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }
      jiraProjectIdDirtyRef.current = false;
      setSavedDataSourceType(selectedDataSource);
      setSavedIsMasterService(isMasterServiceDraft);
      if (!isMasterServiceDraft) {
        resetConfigPanel();
        setMappingValuesDialogOpen(false);
        setSelectedConfigRowForValues(null);
        setMappingValues([]);
        setMappingValuesError(null);
      }
      await fetchMappingData();
      return true;
    } catch (e) {
      setMappingError(e.message || 'Unable to save the data source.');
      return false;
    } finally {
      setMappingSaving(false);
    }
  };

  const handleSaveMappingDialog = () => {
    if (!id) return;
    if (!savedIsMasterService) {
      setMappingDialogError('Master service must be enabled to modify sheet mappings.');
      return;
    }
    const fileUrl = mappingDialogForm.file_url.trim();
    const fileName = mappingDialogForm.file_name.trim();
    const isSlaDialog = mappingDialogType === 'sla';
    // SLA mapping: Ticketing System non previsto; Data Structure obbligatoria (fissata alla creazione).
    const ticketingSystem = isSlaDialog ? '' : mappingDialogForm.ticketing_system.trim();
    const dataStructure = mappingDialogForm.data_structure;

    if (!fileUrl || !fileName || (!isSlaDialog && !ticketingSystem) || (isSlaDialog && !dataStructure)) {
      setMappingDialogError('Please fill in all required fields.');
      return;
    }

    // Frontend-only validation for now (backend not available yet).
    if (!/^https?:\/\/.+/i.test(fileUrl)) {
      setMappingDialogError('File URL must be a valid http/https URL.');
      return;
    }

    setMappingSaving(true);
    fetch(API_ENDPOINTS.SHEET_MAPPING, {
      method: editingSheetMappingId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(editingSheetMappingId ? { id: editingSheetMappingId } : {
          project_id: parseInt(id, 10),
          mapping_type: mappingDialogType,
          ...(isSlaDialog ? { data_structure: dataStructure } : {}),
        }),
        file_url: fileUrl,
        file_name: fileName,
        ticketing_system: ticketingSystem || null,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error((await response.text()) || `Save failed (${response.status})`);
        }
        setMappingDialogOpen(false);
        setMappingDialogError(null);
        await fetchMappingData();
      })
      .catch((e) => {
        setMappingDialogError(e.message || 'Unable to save mapping.');
      })
      .finally(() => {
        setMappingSaving(false);
      });
  };

  const handleEditSheetMapping = (item) => {
    setEditingSheetMappingId(item.id);
    setMappingDialogType(item.mapping_type);
    setMappingDialogForm({
      file_url: item.file_url || '',
      file_name: item.file_name || '',
      ticketing_system: item.ticketing_system || '',
      data_structure: item.data_structure || '',
    });
    setMappingDialogError(null);
    setMappingDialogOpen(true);
  };

  const handleDeleteSheetMapping = async (item) => {
    if (!savedIsMasterService) {
      setMappingError('Master service must be enabled to delete sheet mappings.');
      return;
    }
    setConfirmDialogTitle('Delete mapping');
    setConfirmDialogMessage(`Delete mapping "${item.file_name}"?`);
    setConfirmDialogAction(() => async () => {
      setMappingSaving(true);
      try {
        const response = await fetch(API_ENDPOINTS.SHEET_MAPPING, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.id }),
        });
        if (!response.ok) {
          throw new Error((await response.text()) || `Delete failed (${response.status})`);
        }
        await fetchMappingData();
        setMappingToast({ open: true, message: 'Mapping deleted successfully.', severity: 'success' });
      } catch (e) {
        setMappingError(e.message || 'Unable to delete mapping.');
        setMappingToast({ open: true, message: 'Unable to delete the mapping.', severity: 'error' });
      } finally {
        setMappingSaving(false);
      }
    });
    setConfirmDialogOpen(true);
  };

  const resetConfigPanel = () => {
    setSelectedSheetMapping(null);
    setConfigRows([]);
    setConfigDialogError(null);
    setConfigInitialSnapshot('');
    setConfigSearchTerm('');
    setConfigFilter('all');
    setMissingMappingAlertByField({});
  };

  const handleOpenConfigDialog = async (sheetMapping) => {
    setSelectedSheetMapping(sheetMapping);
    setConfigDialogLoading(true);
    setConfigDialogError(null);
    try {
      const [defsRes, cfgRes] = await Promise.all([
        fetch(
          `${API_ENDPOINTS.FIELD_DEFINITIONS}?applies_to=${sheetMapping.mapping_type}` +
          (sheetMapping.data_structure ? `&data_structure=${encodeURIComponent(sheetMapping.data_structure)}` : '')
        ),
        fetch(`${API_ENDPOINTS.FIELD_CONFIG}?sheet_mapping_id=${sheetMapping.id}`),
      ]);
      if (!defsRes.ok) {
        throw new Error((await defsRes.text()) || `Unable to load field definitions (${defsRes.status})`);
      }
      if (!cfgRes.ok) {
        throw new Error((await cfgRes.text()) || `Unable to load field configuration (${cfgRes.status})`);
      }

      const defsPayload = await defsRes.json();
      const cfgPayload = await cfgRes.json();
      const definitions = parseListPayload(defsPayload, ['items', 'data']);
      const existingConfigs = parseListPayload(cfgPayload, ['items', 'data']);
      const byFieldKey = new Map(existingConfigs.map((row) => [row.field_key, row]));

      const rows = definitions.map((def) => {
        const existing = byFieldKey.get(def.field_key);
        const allowedLogics = Array.isArray(def.allowed_logics) ? def.allowed_logics : [];
        const defaultLogic = allowedLogics.length === 1 ? allowedLogics[0] : '';
        return {
          field_key: def.field_key,
          label: def.label,
          is_required: !!def.is_required,
          allowed_logics: allowedLogics,
          lookup_domain: def.lookup_domain || null,
          allow_multiple: !!def.allow_multiple,
          help_text: def.help_text || '',
          config_id: existing?.id || null,
          source_column_name: existing?.source_column_name || '',
          mapping_logic: existing?.mapping_logic || defaultLogic,
          single_value: existing?.single_value || '',
        };
      });
      setConfigRows(rows);
      setConfigInitialSnapshot(buildConfigSnapshot(rows));
      setConfigSearchTerm('');
      setConfigFilter('all');

      const mappingRowsWithConfig = rows.filter((row) => row.mapping_logic === 'mapping' && !!row.config_id);
      if (mappingRowsWithConfig.length > 0) {
        const missingEntries = await Promise.all(
          mappingRowsWithConfig.map(async (row) => {
            try {
              const res = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${row.config_id}`);
              if (!res.ok) return [row.field_key, false];
              const payload = await res.json();
              const values = parseListPayload(payload, ['items', 'data']);
              return [row.field_key, !Array.isArray(values) || values.length === 0];
            } catch {
              return [row.field_key, false];
            }
          })
        );
        setMissingMappingAlertByField(Object.fromEntries(missingEntries));
      } else {
        setMissingMappingAlertByField({});
      }

      // Preload lookup options for rows configured as choice / dimension (by domain, deduplicated).
      const domainsToLoad = new Set();
      for (const row of rows) {
        if (row.mapping_logic === 'choice' || row.mapping_logic === 'dimension') {
          domainsToLoad.add(getLookupDomainForRow(row));
        }
      }
      // The shared 'dimension' domain is always refreshed, as before.
      domainsToLoad.add('dimension');
      for (const domain of domainsToLoad) {
        // eslint-disable-next-line no-await-in-loop
        await ensureLookupOptions(domain, { force: domain === 'dimension' });
      }
    } catch (e) {
      setConfigDialogError(e.message || 'Unable to load field configuration.');
    } finally {
      setConfigDialogLoading(false);
    }
  };

  const handleCloseConfigDialog = () => {
    if (configRows.length > 0 && buildConfigSnapshot(configRows) !== configInitialSnapshot) {
      setConfirmDialogTitle('Discard unsaved changes');
      setConfirmDialogMessage('You have unsaved changes. Close without saving?');
      setConfirmDialogAction(() => async () => {
        resetConfigPanel();
      });
      setConfirmDialogOpen(true);
      return;
    }
    resetConfigPanel();
  };

  const handleConfirmDialogClose = () => {
    if (confirmDialogBusy) return;
    setConfirmDialogOpen(false);
    setConfirmDialogTitle('');
    setConfirmDialogMessage('');
    setConfirmDialogAction(null);
  };

  const handleConfirmDialogConfirm = async () => {
    if (!confirmDialogAction) {
      handleConfirmDialogClose();
      return;
    }
    setConfirmDialogBusy(true);
    try {
      await confirmDialogAction();
      setConfirmDialogOpen(false);
      setConfirmDialogTitle('');
      setConfirmDialogMessage('');
      setConfirmDialogAction(null);
    } finally {
      setConfirmDialogBusy(false);
    }
  };

  const handleConfigRowChange = (fieldKey, fieldName, value) => {
    setConfigRows((prev) =>
      prev.map((row) => {
        if (row.field_key !== fieldKey) return row;
        if (fieldName === 'mapping_logic') {
          const nextLogic = value;
          return {
            ...row,
            mapping_logic: nextLogic,
            // For mapping logic, single_value is not applicable.
            single_value: (nextLogic === 'mapping' || nextLogic === 'move') ? '' : row.single_value,
          };
        }
        return { ...row, [fieldName]: value };
      })
    );
    if (fieldName === 'mapping_logic' && value !== 'mapping') {
      setMissingMappingAlertByField((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const getSingleValuePlaceholder = (mappingLogic) => {
    switch (mappingLogic) {
      case 'mapping':
        return 'Not applicable';
      case 'move':
        return 'Not applicable';
      case 'choice':
        return 'Required*';
      case 'dimension':
        return 'Required*';
      case 'constant':
        return 'Required*';
      case 'format':
        return 'Required*';
      default:
        return '';
    }
  };

  // Dominio di ref_lookup_values per una riga di configurazione:
  // lookup_domain se valorizzato, altrimenti 'dimension' (logica dimension) o field_key (choice/mapping).
  const getLookupDomainForRow = (row) => {
    if (row.lookup_domain) return row.lookup_domain;
    return row.mapping_logic === 'dimension' ? 'dimension' : row.field_key;
  };

  const ensureLookupOptions = async (domain, { force = false } = {}) => {
    if (!domain) return;
    if (!force && lookupOptionsByDomain[domain]) return;
    try {
      const resp = await fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=${encodeURIComponent(domain)}`);
      if (!resp.ok) return;
      const payload = await resp.json();
      const items = parseListPayload(payload, ['items', 'values', 'data']);
      setLookupOptionsByDomain((prev) => ({ ...prev, [domain]: items }));
    } catch {
      // no-op fallback: keep empty options
    }
  };

  const isSingleValueRequiredByLogic = (logic) => (
    logic === 'constant' || logic === 'format' || logic === 'choice' || logic === 'dimension'
  );

  const isRowBlank = (row) => (
    !String(row.source_column_name || '').trim() &&
    !String(row.mapping_logic || '').trim() &&
    !String(row.single_value || '').trim()
  );

  const hasAnyRowInput = (row) => !isRowBlank(row);

  const isRowConfigured = (row) => {
    if (!row.source_column_name.trim() || !row.mapping_logic) return false;
    if (isSingleValueRequiredByLogic(row.mapping_logic)) {
      return !!String(row.single_value || '').trim();
    }
    return true;
  };

  const getRowStatus = (row) => {
    if (isRowConfigured(row)) return { key: 'configured', label: 'Configured', color: 'success' };
    if (hasAnyRowInput(row)) return { key: 'invalid', label: 'Incomplete', color: 'warning' };
    return { key: 'empty', label: 'Not configured', color: 'default' };
  };

  const buildConfigSnapshot = (rows) => JSON.stringify(
    rows.map((row) => ({
      field_key: row.field_key,
      source_column_name: row.source_column_name,
      mapping_logic: row.mapping_logic,
      single_value: row.single_value || '',
      config_id: row.config_id || null,
    }))
  );

  const formatLookupWithGroup = (item) => {
    if (!item) return '';
    const base = item.label || item.value || '';
    return item.group_value ? `${base} (${item.group_value})` : base;
  };

  // silent=true: skip setting configDialogError (caller collects errors itself).
  const handleSaveConfigRow = async (row, { silent = false } = {}) => {
    if (!selectedSheetMapping) return false;
    if (!savedIsMasterService) {
      if (!silent) setConfigDialogError('Master service must be enabled to modify field configuration.');
      return false;
    }
    if (!row.source_column_name.trim() || !row.mapping_logic) {
      if (!silent) setConfigDialogError(`"${row.label}" requires Source Column Name and Mapping Logic.`);
      return false;
    }
    if (!silent) setConfigDialogError(null);
    try {
      const payload = row.config_id
        ? {
            id: row.config_id,
            source_column_name: row.source_column_name.trim(),
            mapping_logic: row.mapping_logic,
            single_value: row.single_value?.trim() || null,
          }
        : {
            sheet_mapping_id: selectedSheetMapping.id,
            field_key: row.field_key,
            source_column_name: row.source_column_name.trim(),
            mapping_logic: row.mapping_logic,
            single_value: row.single_value?.trim() || null,
          };

      const response = await fetch(API_ENDPOINTS.FIELD_CONFIG, {
        method: row.config_id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }

      const savedPayload = await response.json();
      const savedItem = savedPayload?.item || savedPayload;
      setConfigRows((prev) =>
        prev.map((x) =>
          x.field_key === row.field_key
            ? {
                ...x,
                config_id: savedItem?.id || x.config_id,
                source_column_name: savedItem?.source_column_name ?? x.source_column_name,
                mapping_logic: savedItem?.mapping_logic ?? x.mapping_logic,
                single_value: savedItem?.single_value ?? x.single_value,
              }
            : x
        )
      );
      return savedItem || true;
    } catch (e) {
      if (!silent) setConfigDialogError(e.message || `Unable to save the field "${row.label}".`);
      return false;
    }
  };

  const handleSaveAllConfigRows = async ({ finalize = false } = {}) => {
    if (!selectedSheetMapping) return;
    if (!savedIsMasterService) {
      setConfigDialogError('Master service must be enabled to modify field configuration.');
      return;
    }
    setConfigDialogError(null);

    const SINGLE_VALUE_LOGICS = new Set(['constant', 'format', 'choice', 'dimension']);

    // In draft mode: include only rows that have all the fields required by the backend
    // (source_column_name + mapping_logic + single_value when the logic requires it).
    // Rows that aren't filled yet are simply skipped — not treated as errors.
    // In finalize mode: include all required rows and all rows with any input, then validate below.
    const rowsToSave = finalize
      ? configRows.filter((row) => row.is_required || !!String(row.source_column_name || '').trim())
      : configRows.filter((row) => {
          if (!String(row.source_column_name || '').trim()) return false;
          if (!String(row.mapping_logic || '').trim()) return false;
          if (SINGLE_VALUE_LOGICS.has(row.mapping_logic) && !String(row.single_value || '').trim()) return false;
          return true;
        });

    if (rowsToSave.length === 0) {
      setConfigDialogError(
        finalize
          ? 'No fields to save. Configure at least one field before continuing.'
          : 'No fields are ready to be saved. Enter at least Source Column Name and Mapping Logic for one or more fields.'
      );
      return;
    }

    // Full validation only when finalizing (Save & Finalize), not for Save Draft.
    if (finalize) {
      const invalidRequired = configRows.find(
        (row) => row.is_required && !isRowConfigured(row)
      );
      if (invalidRequired) {
        setConfigDialogError(`Required field incomplete: "${invalidRequired.label}".`);
        return;
      }

      const invalidRows = rowsToSave.filter(
        (row) => !String(row.source_column_name || '').trim() || !String(row.mapping_logic || '').trim()
      );
      if (invalidRows.length > 0) {
        const names = invalidRows.map((r) => `"${r.label}"`).join(', ');
        setConfigDialogError(`Source Column Name and Mapping Logic are required for: ${names}.`);
        return;
      }

      const invalidSingleValueRows = rowsToSave.filter(
        (row) => SINGLE_VALUE_LOGICS.has(row.mapping_logic) && !String(row.single_value || '').trim()
      );
      if (invalidSingleValueRows.length > 0) {
        const names = invalidSingleValueRows.map((r) => `"${r.label}"`).join(', ');
        setConfigDialogError(`Single Value is required for: ${names}.`);
        return;
      }
    }

    setConfigDialogSavingAll(true);
    const failedLabels = [];

    // If an optional persisted field has no Source Column Name, remove it from DB.
    // mapping_logic may be preselected, so source_column_name is the real opt-in signal.
    const rowsToDelete = configRows.filter(
      (row) => !!row.config_id && !row.is_required && !String(row.source_column_name || '').trim()
    );
    const deletedFieldKeys = new Set();
    for (const row of rowsToDelete) {
      try {
        const resp = await fetch(API_ENDPOINTS.FIELD_CONFIG, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: row.config_id }),
        });
        if (!resp.ok) {
          failedLabels.push(row.label);
          continue;
        }
        setConfigRows((prev) =>
          prev.map((x) =>
            x.field_key === row.field_key
              ? { ...x, config_id: null, source_column_name: '', mapping_logic: '', single_value: '' }
              : x
          )
        );
        deletedFieldKeys.add(row.field_key);
      } catch {
        failedLabels.push(row.label);
      }
    }

    const savedByFieldKey = {};
    for (const row of rowsToSave) {
      const savedItem = await handleSaveConfigRow(row, { silent: true });
      if (!savedItem) {
        failedLabels.push(row.label);
      } else if (savedItem !== true) {
        savedByFieldKey[row.field_key] = savedItem;
      }
    }
    setConfigDialogSavingAll(false);
    const failed = failedLabels.length;

    if (failed === 0) {
      const finalRows = configRows.map((row) => {
        if (deletedFieldKeys.has(row.field_key)) {
          return { ...row, config_id: null, source_column_name: '', mapping_logic: '', single_value: '' };
        }
        const savedItem = savedByFieldKey[row.field_key];
        if (!savedItem) return row;
        return {
          ...row,
          config_id: savedItem.id || row.config_id,
          source_column_name: savedItem.source_column_name ?? row.source_column_name,
          mapping_logic: savedItem.mapping_logic ?? row.mapping_logic,
          single_value: savedItem.single_value ?? row.single_value,
        };
      });

      // Non-blocking warning: mapping logic selected but mapping values not configured yet.
      const mappingRows = finalRows.filter(
        (row) => row.mapping_logic === 'mapping' && !!row.config_id
      );
      let missingMappingValuesCount = 0;
      const missingByField = {};
      for (const row of mappingRows) {
        try {
          const res = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${row.config_id}`);
          if (!res.ok) {
            continue;
          }
          const payload = await res.json();
          const values = parseListPayload(payload, ['items', 'data']);
          const isMissing = !Array.isArray(values) || values.length === 0;
          missingByField[row.field_key] = isMissing;
          if (isMissing) {
            missingMappingValuesCount += 1;
          }
        } catch {
          // Ignore single-row check failures: this warning is informational only.
        }
      }
      setMissingMappingAlertByField((prev) => ({ ...prev, ...missingByField }));

      setConfigRows(finalRows);
      setConfigDialogError(null);
      setConfigInitialSnapshot(buildConfigSnapshot(finalRows));

      // When finalizing, mark the sheet mapping as is_draft=false.
      let finalizeError = null;
      if (finalize) {
        try {
          const putRes = await fetch(API_ENDPOINTS.SHEET_MAPPING, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: selectedSheetMapping.id,
              file_url: selectedSheetMapping.file_url,
              file_name: selectedSheetMapping.file_name,
              ticketing_system: selectedSheetMapping.ticketing_system,
              is_draft: false,
            }),
          });
          if (putRes.ok) {
            const putData = await putRes.json();
            const updatedMapping = putData.item ?? { ...selectedSheetMapping, is_draft: false };
            setSelectedSheetMapping(updatedMapping);
            setMappingItems((prev) =>
              prev.map((m) => (m.id === updatedMapping.id ? updatedMapping : m))
            );
          } else if (selectedSheetMapping.mapping_type === 'sla') {
            // SLA sheets are validated server-side on finalize (required fields, SLA rule).
            let message = '';
            try {
              const errPayload = await putRes.json();
              message = errPayload?.error || '';
            } catch {
              message = '';
            }
            finalizeError = message || `Unable to finalize the SLA mapping (${putRes.status}).`;
          }
        } catch {
          // Non-blocking: fields are saved; only the draft flag update failed.
        }
      }

      if (finalizeError) {
        setConfigDialogError(finalizeError);
        setMappingToast({
          open: true,
          message: `Fields saved, but the mapping is still in draft. ${finalizeError}`,
          severity: 'warning',
        });
      } else if (missingMappingValuesCount > 0) {
        setMappingToast({
          open: true,
          message: `Configuration saved. ${missingMappingValuesCount} field(s) use Mapping logic but have no Mapping Values configured yet.`,
          severity: 'warning',
        });
      } else {
        setMappingToast({
          open: true,
          message: finalize ? 'Configuration finalized successfully.' : 'Draft saved successfully.',
          severity: 'success',
        });
      }
    } else {
      const names = failedLabels.map((l) => `"${l}"`).join(', ');
      setMappingToast({
        open: true,
        message: `Unable to save ${failedLabels.length} field(s): ${names}. Check the data and try again.`,
        severity: 'warning',
      });
    }
  };

  const handleOpenMappingValuesDialog = async (row) => {
    if (row.mapping_logic !== 'mapping') {
      const msg = `"${row.label}" must use "Mapping" logic to configure mapping values.`;
      setConfigDialogError(msg);
      setMappingToast({ open: true, message: msg, severity: 'warning' });
      return;
    }

    let rowForValues = row;
    if (!rowForValues.config_id) {
      const savedItem = await handleSaveConfigRow(rowForValues);
      if (!savedItem || !savedItem.id) {
        const msg = `Cannot open Mapping Values for "${row.label}". Fill in Source Column Name first.`;
        setConfigDialogError(msg);
        setMappingToast({ open: true, message: msg, severity: 'warning' });
        return;
      }
      rowForValues = {
        ...rowForValues,
        config_id: savedItem.id,
        source_column_name: savedItem.source_column_name ?? rowForValues.source_column_name,
        mapping_logic: savedItem.mapping_logic ?? rowForValues.mapping_logic,
        single_value: savedItem.single_value ?? rowForValues.single_value,
      };
    }

    setSelectedConfigRowForValues(rowForValues);
    setMappingValueForm({ id: null, source_value: '', normalized_ref_id: '' });
    setMappingValues([]);
    setNormalizedOptionsForValues([]);
    setMappingValuesError(null);
    setMappingValuesDialogOpen(true);
    setMappingValuesLoading(true);
    try {
      const [valuesRes, normalizedRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${rowForValues.config_id}`),
        fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=${encodeURIComponent(rowForValues.lookup_domain || rowForValues.field_key)}`),
      ]);
      if (!valuesRes.ok) {
        throw new Error((await valuesRes.text()) || `Unable to load mapping values (${valuesRes.status})`);
      }
      if (!normalizedRes.ok) {
        throw new Error((await normalizedRes.text()) || `Unable to load normalized values (${normalizedRes.status})`);
      }
      const valuesPayload = await valuesRes.json();
      const normalizedPayload = await normalizedRes.json();
      const valuesList = parseListPayload(valuesPayload, ['items', 'data']);
      setMappingValues(valuesList);
      setMissingMappingAlertByField((prev) => ({
        ...prev,
        [rowForValues.field_key]: !Array.isArray(valuesList) || valuesList.length === 0,
      }));
      setNormalizedOptionsForValues(parseListPayload(normalizedPayload, ['items', 'values', 'data']));
    } catch (e) {
      setMappingValuesError(e.message || 'Unable to load mapping values.');
    } finally {
      setMappingValuesLoading(false);
    }
  };

  const handleSaveMappingValue = async () => {
    if (!selectedConfigRowForValues?.config_id) return;
    if (!savedIsMasterService) {
      setMappingValuesError('Master service must be enabled to modify mapping values.');
      return;
    }
    if (!mappingValueForm.source_value.trim() || !mappingValueForm.normalized_ref_id) {
      setMappingValuesError('Please fill in both Source Value and Normalized Value.');
      return;
    }
    setMappingValuesError(null);
    try {
      const payload = mappingValueForm.id
        ? {
            id: mappingValueForm.id,
            source_value: mappingValueForm.source_value.trim(),
            normalized_ref_id: parseInt(mappingValueForm.normalized_ref_id, 10),
          }
        : {
            field_config_id: selectedConfigRowForValues.config_id,
            source_value: mappingValueForm.source_value.trim(),
            normalized_ref_id: parseInt(mappingValueForm.normalized_ref_id, 10),
          };

      const response = await fetch(API_ENDPOINTS.MAPPING_VALUES, {
        method: mappingValueForm.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }
      const refreshed = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${selectedConfigRowForValues.config_id}`);
      if (refreshed.ok) {
        const refreshedPayload = await refreshed.json();
        const refreshedList = parseListPayload(refreshedPayload, ['items', 'data']);
        setMappingValues(refreshedList);
        if (selectedConfigRowForValues?.field_key) {
          setMissingMappingAlertByField((prev) => ({
            ...prev,
            [selectedConfigRowForValues.field_key]: !Array.isArray(refreshedList) || refreshedList.length === 0,
          }));
        }
      }
      setMappingValueForm({ id: null, source_value: '', normalized_ref_id: '' });
    } catch (e) {
      setMappingValuesError(e.message || 'Unable to save the mapping value.');
    }
  };

  const handleEditMappingValue = (item) => {
    setMappingValueForm({
      id: item.id,
      source_value: item.source_value || '',
      normalized_ref_id: String(item.normalized_ref_id || ''),
    });
  };

  const handleDeleteMappingValue = async (itemId) => {
    if (!selectedConfigRowForValues?.config_id) return;
    if (!savedIsMasterService) {
      setMappingValuesError('Master service must be enabled to modify mapping values.');
      return;
    }
    try {
      const response = await fetch(API_ENDPOINTS.MAPPING_VALUES, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemId }),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Delete failed (${response.status})`);
      }
      const refreshed = await fetch(`${API_ENDPOINTS.MAPPING_VALUES}?field_config_id=${selectedConfigRowForValues.config_id}`);
      if (refreshed.ok) {
        const refreshedPayload = await refreshed.json();
        const refreshedList = parseListPayload(refreshedPayload, ['items', 'data']);
        setMappingValues(refreshedList);
        if (selectedConfigRowForValues?.field_key) {
          setMissingMappingAlertByField((prev) => ({
            ...prev,
            [selectedConfigRowForValues.field_key]: !Array.isArray(refreshedList) || refreshedList.length === 0,
          }));
        }
      }
    } catch (e) {
      setMappingValuesError(e.message || 'Unable to delete the mapping value.');
    }
  };

  // ===== Activities Tab functions =====
  const fetchAllActivities = useCallback(async () => {
    try {
      const resp = await fetch(`${API_ENDPOINTS.ACTIVITIES}?for_project_config=true`);
      if (!resp.ok) return;
      const data = await resp.json();
      setActivities(data.activities || []);
    } catch (e) {
      // silent
    }
  }, []);

  const fetchProjectActivities = useCallback(async () => {
    if (!id) return;
    setActivitiesLoading(true);
    setActivitiesError(null);
    try {
      const resp = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${id}`);
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(err || 'Failed to load project activities');
      }
      const data = await resp.json();
      setProjectActivityIds(data.activity_ids || []);
    } catch (e) {
      setActivitiesError(e.message);
    } finally {
      setActivitiesLoading(false);
    }
  }, [id]);

  const toggleActivitySelection = (activityId) => {
    setProjectActivityIds((prev) => {
      const set = new Set(prev);
      if (set.has(activityId)) {
        set.delete(activityId);
      } else {
        set.add(activityId);
      }
      return Array.from(set);
    });
  };

  const handleSaveActivities = async () => {
    if (!id) return;
    setActivitiesSaving(true);
    setActivitiesError(null);
    try {
      const resp = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: parseInt(id, 10), activity_ids: projectActivityIds })
      });
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(err || 'Unable to save the activities');
      }
      await fetchProjectActivities();
    } catch (e) {
      setActivitiesError(e.message);
    } finally {
      setActivitiesSaving(false);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    setFormError(null);
    
    if (!formData.name.trim()) {
      setFormError('Project name is required.');
      setSaving(false);
      return;
    }

    if (!formData.project_code.trim()) {
      setFormError('Project code is required.');
      setSaving(false);
      return;
    }

    if (!formData.customer_id) {
      setFormError('Customer is required.');
      setSaving(false);
      return;
    }

    if (!String(formData.start_date || '').trim()) {
      setFormError('Start date is required.');
      setSaving(false);
      return;
    }

    if (!String(formData.end_date || '').trim()) {
      setFormError('End date is required.');
      setSaving(false);
      return;
    }

    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      setFormError('End date must be on or after start date.');
      setSaving(false);
      return;
    }

    if (!formData.days_after_block || Number(formData.days_after_block) < 1) {
      setFormError('Days after block is required and must be greater than 0.');
      setSaving(false);
      return;
    }

    if (!formData.days_before_block || Number(formData.days_before_block) < 1) {
      setFormError('Days before block is required and must be greater than 0.');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        customer_id: parseInt(formData.customer_id),
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        active: formData.active,
        project_code: formData.project_code.trim(),
        days_after_block: parseInt(formData.days_after_block) || 1,
        days_before_block: parseInt(formData.days_before_block) || 1,
        ticket_id_prefix: formData.ticket_id_prefix.trim() || null,
        regex: formData.regex.trim() || null,
        regex_desc: formData.regex_desc.trim() || null,
        previous_month_blocked: formData.previous_month_blocked,
        yubiq_code: formData.yubiq_code || null,
        tipologia_servizio: formData.tipologia_servizio || null,
        ...(backendUser?.id ? { user_id: backendUser.id } : {}),
      };

      console.log('[PROJECT SAVE] Payload dates being sent to backend:', {
        start_date: payload.start_date,
        end_date: payload.end_date
      });
      console.log('[PROJECT] Update payload:', payload);

      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
      }

      const result = await response.json();
      console.log('[PROJECT] Update result:', result);

      // Refresh project data
      await fetchProject();
      
      // Close dialog
      setOpenDialog(false);
      
    } catch (err) {
      console.error('[PROJECT] Update error:', err);
      setFormError(`Error updating project: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ===== Contract Tab functions =====
  const fetchContractData = useCallback(async () => {
    if (!id) return;
    setContractLoading(true);
    setContractError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.REVENUES}?project_id=${id}`);
      if (!response.ok) {
        throw new Error(`Unable to load contract (${response.status})`);
      }
      const data = await response.json();
      const contract = data.contract || null;
      setContractData(contract);
      if (contract) {
        setContractForm({
          tipologia_servizio: contract.tipologia_servizio || '',
          ricavi_actual: contract.ricavi_actual != null ? String(contract.ricavi_actual) : '',
          ricavi_anno_prec: contract.ricavi_anno_prec != null ? String(contract.ricavi_anno_prec) : '',
          ricavi_anno_corrente: contract.ricavi_anno_corrente != null ? String(contract.ricavi_anno_corrente) : '',
          ricavi_anno_succ: contract.ricavi_anno_succ != null ? String(contract.ricavi_anno_succ) : '',
          canone_mensile: contract.canone_mensile != null ? String(contract.canone_mensile) : '',
        });
        setContractEditMode(false);
      } else {
        setContractForm({
          tipologia_servizio: '',
          ricavi_actual: '', ricavi_anno_prec: '', ricavi_anno_corrente: '',
          ricavi_anno_succ: '', canone_mensile: '',
        });
        setContractEditMode(false);
      }
    } catch (e) {
      setContractError(e.message || 'Unable to load contract.');
    } finally {
      setContractLoading(false);
    }
  }, [id]);

  const handleContractFormChange = (e) => {
    const { name, value } = e.target;
    setContractForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveContract = async () => {
    if (!id) return;
    setContractSaving(true);
    setContractError(null);
    try {
      const payload = {
        project_id: parseInt(id, 10),
        ricavi_actual: contractForm.ricavi_actual ? parseFloat(contractForm.ricavi_actual) : null,
        ricavi_anno_prec: contractForm.ricavi_anno_prec ? parseFloat(contractForm.ricavi_anno_prec) : null,
        ricavi_anno_corrente: contractForm.ricavi_anno_corrente ? parseFloat(contractForm.ricavi_anno_corrente) : null,
        ricavi_anno_succ: contractForm.ricavi_anno_succ ? parseFloat(contractForm.ricavi_anno_succ) : null,
        canone_mensile: contractForm.canone_mensile ? parseFloat(contractForm.canone_mensile) : null,
        ...(backendUser?.id ? { user_id: backendUser.id } : {}),
      };
      const response = await fetch(API_ENDPOINTS.REVENUES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error((await response.text()) || `Save failed (${response.status})`);
      }
      await fetchContractData();
      setContractToast({ open: true, message: 'Revenue saved successfully.', severity: 'success' });
      setContractEditMode(false);
    } catch (e) {
      setContractError(e.message || 'Unable to save revenue.');
      setContractToast({ open: true, message: 'Unable to save revenue.', severity: 'error' });
    } finally {
      setContractSaving(false);
    }
  };

  const buildContractRow = (contract) => {
    const currentYear = new Date().getFullYear();
    return {
      'Customer': contract?.cliente || '',
      'Project name': contract?.servizio || '',
      'Project Code': contract?.project_code || '',
      'Yubiq Code': contract?.yubiq_code || '',
      'Service Type': contract?.tipologia_servizio || '',
      'Surplus/Deficit vs Actual Revenues': contract?.ricavi_actual ?? '',
      [`Revenue ${currentYear - 1} (year-1)`]: contract?.ricavi_anno_prec ?? '',
      [`Revenue ${currentYear} (current year)`]: contract?.ricavi_anno_corrente ?? '',
      [`Revenue ${currentYear + 1} (year+1)`]: contract?.ricavi_anno_succ ?? '',
      'Current Monthly Fee': contract?.canone_mensile ?? '',
    };
  };

  const handleExportContract = () => {
    if (!contractData) return;
    const exportData = {
      ...contractData,
      cliente: contractData?.cliente || project?.customer?.name || '',
      servizio: contractData?.servizio || project?.name || '',
      project_code: contractData?.project_code || project?.project_code || '',
      yubiq_code: contractData?.yubiq_code ?? project?.yubiq_code ?? '',
      tipologia_servizio: contractData?.tipologia_servizio ?? project?.tipologia_servizio ?? '',
    };
    const dataToExport = [buildContractRow(exportData)];
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Revenues');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `revenues_${project?.name || 'project'}.xlsx`);
  };

  const handleExportAllContracts = async () => {
    if (!backendUser?.id) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.REVENUES}?user_id=${backendUser.id}`);
      if (!res.ok) {
        let details = '';
        try {
          const errPayload = await res.json();
          details = errPayload?.error || '';
        } catch {
          details = await res.text();
        }
        throw new Error(details ? `Unable to load contracts for export: ${details}` : 'Unable to load contracts for export.');
      }
      const data = await res.json();
      const contracts = data.contracts || [];
      if (contracts.length === 0) {
        setContractToast({ open: true, message: 'No contracts found for export.', severity: 'info' });
        return;
      }
      const dataToExport = contracts.map(buildContractRow);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Revenues');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, 'revenues_export.xlsx');
    } catch (e) {
      setContractToast({ open: true, message: e.message || 'Export failed.', severity: 'error' });
    }
  };

  const formatDateTimeDisplay = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const renderContractAuditUser = (username, email) => {
    const u = (username || '').trim();
    const e = (email || '').trim();
    if (!u && !e) {
      return <Typography sx={{ fontSize: '1rem', color: 'text.primary' }}>-</Typography>;
    }
    return (
      <Box>
        {u ? (
          <Typography sx={{ fontSize: '1rem', color: 'text.primary', lineHeight: 1.4 }}>{u}</Typography>
        ) : null}
        {e ? (
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              lineHeight: 1.4,
              mt: u ? 0.25 : 0,
            }}
          >
            {e}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const formatDateDisplay = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // ===== useEffect hooks =====
  useEffect(() => {
    if (id) {
      fetchProject();
      fetchCustomers();
      fetchAllActivities();
      fetchProjectActivities();
      fetchTypeOptions();
    }
  }, [id, fetchProject, fetchCustomers, fetchAllActivities, fetchProjectActivities, fetchTypeOptions]);

  useEffect(() => {
    if (ENABLE_TAB_MAPPING && currentTab === TAB_INDEX.MAPPING && id) {
      // Re-initialize from backend each time the user opens the Mapping tab.
      mappingFormInitializedRef.current = false;
      fetchMappingData();
    }
  }, [currentTab, id, fetchMappingData]);

  useEffect(() => {
    if (ENABLE_TAB_CONTRACT && currentTab === TAB_INDEX.CONTRACT && id) {
      fetchContractData();
    }
  }, [currentTab, id, fetchContractData]);

  const configHasUnsavedChanges = configRows.length > 0 && buildConfigSnapshot(configRows) !== configInitialSnapshot;
  const normalizedSearch = configSearchTerm.trim().toLowerCase();
  const filteredConfigRows = configRows.filter((row) => {
    const matchesSearch = !normalizedSearch || row.label.toLowerCase().includes(normalizedSearch);
    if (!matchesSearch) return false;

    if (configFilter === 'required') return row.is_required;
    if (configFilter === 'optional') return !row.is_required;
    if (configFilter === 'configured') return isRowConfigured(row);
    if (configFilter === 'not_configured') return !isRowConfigured(row);
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          variant="outlined"
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box my={4}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Project not found
        </Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          variant="outlined"
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box my={4}>
        {/* Header con titolo, back button e edit button */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontSize: '1.5rem', flexGrow: 1 }}>
            Project Details for
            <Box
              component="span"
              sx={{
                display: { xs: 'block', md: 'inline' },
                bgcolor: '#e0e0e0',
                color: '#607c89',
                fontWeight: 500,
                fontSize: '0.9em',
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                ml: { xs: 0, md: 1 },
                mt: { xs: 1, md: 0 }
              }}
            >
              {project.name}
            </Box>
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={handleEditDialog} 
            sx={{
              ml: 2,
              bgcolor: RUN_BRAND_GREEN,
              color: '#fff',
              '&:hover': { bgcolor: RUN_BRAND_GREEN_HOVER },
            }}
          >
            Edit Project Details
          </Button>
        </Box>

        {/* Tabs Navigation */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[1],
          }}
        >
          <Box
            sx={{
              px: { xs: 0.5, sm: 1.5 },
              pt: 1.25,
              pb: 0.5,
              bgcolor: 'background.paper',
            }}
          >
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              aria-label="project information tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ sx: { display: 'none' } }}
              sx={{
                minHeight: 52,
                '& .MuiTabs-flexContainer': {
                  alignItems: 'center',
                  gap: { xs: 0, sm: 0.25 },
                },
                '& .MuiTabs-scrollButtons': {
                  color: 'text.secondary',
                  '&.Mui-disabled': { opacity: 0.35 },
                },
              }}
            >
              <Tab 
                icon={<InfoIcon />}
                label="General" 
                id="project-tab-0"
                aria-controls="project-tabpanel-0"
                iconPosition="start"
                sx={PROJECT_DETAIL_TAB_SX}
              />

              <Tab 
                icon={<PeopleIcon />}
                label="Users" 
                id="project-tab-1"
                aria-controls="project-tabpanel-1"
                iconPosition="start"
                sx={PROJECT_DETAIL_TAB_SX}
              />
              <Tab 
                icon={<GroupsIcon />}
                label="Teams" 
                id="project-tab-2"
                aria-controls="project-tabpanel-2"
                iconPosition="start"
                sx={PROJECT_DETAIL_TAB_SX}
              />
              <Tab 
                icon={<ChecklistIcon />}
                label="Activities" 
                id="project-tab-3"
                aria-controls="project-tabpanel-3"
                iconPosition="start"
                sx={PROJECT_DETAIL_TAB_SX}
              />
              {ENABLE_TAB_CONTRACT && (
                <Tab
                  icon={<DescriptionIcon />}
                  label="Revenues"
                  id={`project-tab-${TAB_INDEX.CONTRACT}`}
                  aria-controls={`project-tabpanel-${TAB_INDEX.CONTRACT}`}
                  iconPosition="start"
                  sx={PROJECT_DETAIL_TAB_SX}
                />
              )}
              {ENABLE_TAB_MAPPING && (
                <Tab
                  icon={<SwapHorizIcon />}
                  label="Report Mapping"
                  id={`project-tab-${TAB_INDEX.MAPPING}`}
                  aria-controls={`project-tabpanel-${TAB_INDEX.MAPPING}`}
                  iconPosition="start"
                  sx={PROJECT_DETAIL_TAB_SX}
                />
              )}
            </Tabs>
          </Box>

          {/* Tab Panel 1: General Information */}
          <ProjectDetailTabPanel value={currentTab} index={0}>
            <Stack spacing={2.5} sx={{ maxWidth: 1000, mx: 'auto' }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <BusinessOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                    Core details
                  </Typography>
                </Stack>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Project name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                      {project.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Project code
                    </Typography>
                    <Chip
                      label={project.project_code || 'N/A'}
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Customer
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {project.customer?.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Status
                    </Typography>
                    <Chip
                      label={project.active ? 'Active' : 'Inactive'}
                      color={project.active ? 'success' : 'default'}
                      variant={project.active ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Grid>
                  {ENABLE_TAB_CONTRACT && (<>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Yubiq code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {project.yubiq_code || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Service type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {project.tipologia_servizio || 'N/A'}
                    </Typography>
                  </Grid>
                  </>)}
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <ConfirmationNumberOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                    Ticket ID and validation
                  </Typography>
                </Stack>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Ticket ID prefix
                    </Typography>
                    {project.ticket_id_prefix ? (
                      <Typography
                        component="code"
                        variant="body2"
                        sx={{
                          display: 'inline-block',
                          px: 1.25,
                          py: 0.75,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        {project.ticket_id_prefix}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Not set
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Regex pattern
                    </Typography>
                    {project.regex ? (
                      <Typography
                        component="code"
                        variant="body2"
                        sx={{
                          display: 'block',
                          px: 1.25,
                          py: 0.75,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        {project.regex}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Not set
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Regex description (shown to users)
                    </Typography>
                    {project.regex_desc ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {project.regex_desc}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No description provided
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                    Schedule
                  </Typography>
                </Stack>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Start date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatSimpleDate(project.start_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      End date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatSimpleDate(project.end_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Duration
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {project.start_date && project.end_date
                        ? (() => {
                            const start = new Date(project.start_date);
                            const end = new Date(project.end_date);
                            const diffTime = Math.abs(end - start);
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            return `${diffDays} days`;
                          })()
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <TuneOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                    Worklog time window
                  </Typography>
                </Stack>
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 1.5 }}>
                  Users can add or edit worklogs within this many days before and after today.
                </Alert>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  <Chip
                    label={`${project.days_before_block || 1} day(s) before`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                  <Chip
                    label={`${project.days_after_block || 1} day(s) after`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox checked={!!project.previous_month_blocked} disabled size="small" />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                        Block previous month
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        When enabled, the previous calendar month cannot be edited in worklogs.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <DescriptionIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                    Additional
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: project.description ? 'normal' : 'italic',
                    color: project.description ? 'text.primary' : 'text.secondary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {project.description || 'No description provided'}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <InfoIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                    Record details
                  </Typography>
                </Stack>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Project ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {project.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Created at
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(project.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.04, textTransform: 'uppercase' }}>
                      Last updated
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(project.updated_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </ProjectDetailTabPanel>



          {/* Tab Panel 2: Users Management */}
          <ProjectDetailTabPanel value={currentTab} index={1}>
            <ProjectUsers projectId={id} projectName={project.name} />
          </ProjectDetailTabPanel>

          {/* Tab Panel 3: Teams Management */}
          <ProjectDetailTabPanel value={currentTab} index={2}>
            <ProjectTeams projectId={id} projectName={project.name} />
          </ProjectDetailTabPanel>

          {/* Tab Panel 4: Activities */}
          <ProjectDetailTabPanel value={currentTab} index={3}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Activities</Typography>
              {activitiesError && (
                <Alert severity="error" sx={{ mb: 2 }}>{activitiesError}</Alert>
              )}
              {activitiesLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading activities...</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                  {activities.map((act) => (
                    <FormControlLabel
                      key={act.id}
                      control={
                        <Checkbox
                          checked={projectActivityIds.includes(act.id)}
                          onChange={() => toggleActivitySelection(act.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{act.name}</Typography>
                          {act.description && (
                            <Typography variant="caption" color="text.secondary">{act.description}</Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Button
                  onClick={handleSaveActivities}
                  variant="contained"
                  startIcon={activitiesSaving ? <CircularProgress size={16} /> : <ScheduleIcon />}
                  disabled={activitiesSaving}
                  sx={{ backgroundColor: '#388e3c', '&:hover': { backgroundColor: '#2e7d32' } }}
                >
                  {activitiesSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          </ProjectDetailTabPanel>

          {/* Tab Panel: Contract (feature-flagged) */}
          {ENABLE_TAB_CONTRACT && (
            <ProjectDetailTabPanel value={currentTab} index={TAB_INDEX.CONTRACT}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!contractEditMode ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setContractEditMode(true)}
                      >
                        EDIT REVENUES
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          fetchContractData();
                          setContractEditMode(false);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportContract}
                      disabled={!contractData}
                    >
                      Export Excel
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportAllContracts}
                    >
                      Export All (SM)
                    </Button>
                  </Box>
                </Box>

                {contractError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{contractError}</Alert>
                )}

                {contractLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading contract...</Typography>
                  </Box>
                ) : (
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    {!contractEditMode ? (
                      /* ── Read-only view ─────────────────────────────── */
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* Section 1 – Customer / Project */}
                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                            Customer / Project
                          </Typography>
                          <Divider sx={{ mb: 1.5 }} />
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            {[
                              ['Customer', contractData?.cliente || project?.customer?.name || '-'],
                              ['Project name', contractData?.servizio || project?.name || '-'],
                              ['Project Code', project?.project_code || '-'],
                            ].map(([label, value]) => (
                              <Box key={label} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>{label}</Typography>
                                <Typography sx={{ fontSize: '1rem', color: 'text.primary' }}>{value}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        {/* Section 2 – Economic Data */}
                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                            Economic Data
                          </Typography>
                          <Divider sx={{ mb: 1.5 }} />
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            {[
                              ['Start Date Revenue', formatDateDisplay(project?.start_date) || '-'],
                              ['End Date Revenue', formatDateDisplay(project?.end_date) || '-'],
                              ['Surplus/Deficit vs Actual Revenues (€)', contractForm.ricavi_actual || '-'],
                              [`Revenue ${new Date().getFullYear() - 1} – year-1 (€)`, contractForm.ricavi_anno_prec || '-'],
                              [`Revenue ${new Date().getFullYear()} – current year (€)`, contractForm.ricavi_anno_corrente || '-'],
                              [`Revenue ${new Date().getFullYear() + 1} – year+1 (€)`, contractForm.ricavi_anno_succ || '-'],
                              ...(contractForm.tipologia_servizio === 'Monthly Rate'
                                ? [['Current Monthly Fee', contractForm.canone_mensile || '-']]
                                : []),
                            ].map(([label, value]) => (
                              <Box key={label} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white' }}>
                                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>{label}</Typography>
                                <Typography sx={{ fontSize: '1rem', color: 'text.primary' }}>{value}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        {/* Metadata */}
                        <Box>
                          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                            Record Info
                          </Typography>
                          <Divider sx={{ mb: 1.5 }} />
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            {[
                              { label: 'Created At', value: formatDateTimeDisplay(contractData?.created_at) || '-' },
                              { label: 'Created By', username: contractData?.created_by_username, email: contractData?.created_by_email },
                              { label: 'Last Updated', value: formatDateTimeDisplay(contractData?.updated_at) || '-' },
                              { label: 'Last Updated By', username: contractData?.updated_by_username, email: contractData?.updated_by_email },
                            ].map((field) => (
                              <Box key={field.label} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white' }}>
                                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>{field.label}</Typography>
                                {'username' in field ? (
                                  renderContractAuditUser(field.username, field.email)
                                ) : (
                                  <Typography sx={{ fontSize: '1rem', color: 'text.primary' }}>{field.value}</Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>

                      </Box>
                    ) : (
                      /* ── Edit form ──────────────────────────────────── */
                      <>
                        {/* Section 1 – Customer / Project */}
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                          Customer / Project
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                          <TextField
                            label="Customer"
                            value={contractData?.cliente || project?.customer?.name || ''}
                            size="medium"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: '#f5f5f5' }}
                          />
                          <TextField
                            label="Project name"
                            value={contractData?.servizio || project?.name || ''}
                            size="medium"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: '#f5f5f5' }}
                          />
                          <TextField
                            label="Project Code"
                            value={project?.project_code || ''}
                            size="medium"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: '#f5f5f5' }}
                          />
                        </Box>

                        {/* Section 2 – Economic Data */}
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                          Economic Data
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
                          <TextField
                            label="Start Date Revenue"
                            value={formatDateDisplay(project?.start_date) || ''}
                            size="medium"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: '#f5f5f5' }}
                          />
                          <TextField
                            label="End Date Revenue"
                            value={formatDateDisplay(project?.end_date) || ''}
                            size="medium"
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: '#f5f5f5' }}
                          />
                          <TextField
                            label="Surplus/Deficit vs Actual Revenues (€)"
                            name="ricavi_actual"
                            value={contractForm.ricavi_actual}
                            onChange={handleContractFormChange}
                            type="number"
                            size="medium"
                            fullWidth
                            inputProps={{ step: '0.01' }}
                          />
                          <TextField
                            label={`Revenue ${new Date().getFullYear() - 1} – year-1 (€)`}
                            name="ricavi_anno_prec"
                            value={contractForm.ricavi_anno_prec}
                            onChange={handleContractFormChange}
                            type="number"
                            size="medium"
                            fullWidth
                            inputProps={{ step: '0.01' }}
                          />
                          <TextField
                            label={`Revenue ${new Date().getFullYear()} – current year (€)`}
                            name="ricavi_anno_corrente"
                            value={contractForm.ricavi_anno_corrente}
                            onChange={handleContractFormChange}
                            type="number"
                            size="medium"
                            fullWidth
                            inputProps={{ step: '0.01' }}
                          />
                          <TextField
                            label={`Revenue ${new Date().getFullYear() + 1} – year+1 (€)`}
                            name="ricavi_anno_succ"
                            value={contractForm.ricavi_anno_succ}
                            onChange={handleContractFormChange}
                            type="number"
                            size="medium"
                            fullWidth
                            inputProps={{ step: '0.01' }}
                          />
                          {contractForm.tipologia_servizio === 'Monthly Rate' && (
                            <TextField
                              label="Current Monthly Fee"
                              name="canone_mensile"
                              value={contractForm.canone_mensile}
                              onChange={handleContractFormChange}
                              type="number"
                              size="medium"
                              fullWidth
                              inputProps={{ step: '0.01' }}
                            />
                          )}
                        </Box>

                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            onClick={handleSaveContract}
                            disabled={contractSaving}
                          >
                            {contractSaving ? 'Saving...' : 'Save'}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Paper>
                )}

                <Snackbar
                  open={contractToast.open}
                  autoHideDuration={4000}
                  onClose={() => setContractToast((prev) => ({ ...prev, open: false }))}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                  <Alert
                    onClose={() => setContractToast((prev) => ({ ...prev, open: false }))}
                    severity={contractToast.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                  >
                    {contractToast.message}
                  </Alert>
                </Snackbar>
              </Box>
            </ProjectDetailTabPanel>
          )}

          {/* Tab Panel: Mapping (feature-flagged) */}
          {ENABLE_TAB_MAPPING && (
          <ProjectDetailTabPanel value={currentTab} index={TAB_INDEX.MAPPING}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Mapping</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select a data source for this project. With Master service enabled, enter the Jira Project ID before saving, or save first to configure Google Sheets mappings.
              </Typography>
              {mappingError && (
                <Alert severity="error" sx={{ mb: 2 }}>{mappingError}</Alert>
              )}
              {mappingLoading ? (
                <Box display="flex" alignItems="center" gap={1} sx={{ py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading mapping configuration...</Typography>
                </Box>
              ) : null}

              <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isMasterServiceDraft}
                        onChange={handleMasterServiceChange}
                        disabled={mappingSaving || mappingLoading}
                      />
                    }
                    label="Master service"
                  />
                  <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>Data Source</InputLabel>
                    <Select
                      value={selectedDataSource}
                      label="Data Source"
                      onChange={handleDataSourceChange}
                    >
                      <MenuItem value="jira">JIRA</MenuItem>
                      <MenuItem value="gsheet">Google Sheets</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleSaveDataSource}
                    disabled={mappingSaving || mappingLoading}
                    sx={{ backgroundColor: '#388e3c', '&:hover': { backgroundColor: '#2e7d32' } }}
                  >
                    {mappingSaving ? 'Saving...' : 'Save Data Source'}
                  </Button>
                </Box>
              </Paper>

              {selectedDataSource === 'jira' && isMasterServiceDraft && (
                <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Jira Project ID
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    label="jira_project_id"
                    placeholder="e.g. RUNR or 10023"
                    value={jiraProjectIdDraft}
                    onChange={(e) => {
                      setJiraProjectIdDraft(e.target.value);
                      jiraProjectIdDirtyRef.current = true;
                      mappingFormInitializedRef.current = true;
                    }}
                    onFocus={() => { isEditingJiraProjectIdRef.current = true; }}
                    onBlur={() => { isEditingJiraProjectIdRef.current = false; }}
                  />
                </Paper>
              )}

              {selectedDataSource === 'gsheet' && savedIsMasterService && (
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Google Sheets Mapping
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <Button variant="contained" onClick={handleAddFieldMapping} sx={{ backgroundColor: '#388e3c', '&:hover': { backgroundColor: '#2e7d32' } }}>
                      +ADD Field Mapping
                    </Button>
                    <Button variant="contained" onClick={handleAddSlaMapping} sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
                      +ADD SLA Mapping
                    </Button>
                  </Box>

                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>File URL</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Ticketing System</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappingItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, fontStyle: 'italic' }}>
                                No mappings added yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          mappingItems.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {item.mapping_type === 'field'
                                  ? 'Field Mapping'
                                  : `SLA Mapping${item.data_structure ? ` (${dataStructureOptions.find((opt) => opt.value === item.data_structure)?.label || item.data_structure})` : ''}`}
                              </TableCell>
                              <TableCell>{item.file_name}</TableCell>
                              <TableCell>
                                <Typography
                                  component="a"
                                  href={item.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {item.file_url}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {item.mapping_type === 'sla'
                                  ? '-'
                                  : (ticketingOptions.find((opt) => opt.value === item.ticketing_system)?.label || item.ticketing_system)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={item.is_draft !== false ? 'Draft' : 'Complete'}
                                  color={item.is_draft !== false ? 'warning' : 'success'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpenConfigDialog(item)}
                                  >
                                    Configure
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleEditSheetMapping(item)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    onClick={() => handleDeleteSheetMapping(item)}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {selectedSheetMapping && (
                    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Configure Mapping Fields — {selectedSheetMapping.file_name}
                          {selectedSheetMapping.mapping_type === 'sla' && selectedSheetMapping.data_structure
                            ? ` (SLA · ${dataStructureOptions.find((opt) => opt.value === selectedSheetMapping.data_structure)?.label || selectedSheetMapping.data_structure})`
                            : ''}
                        </Typography>
                        <Chip
                          size="small"
                          label={selectedSheetMapping.is_draft !== false ? 'Draft' : 'Complete'}
                          color={selectedSheetMapping.is_draft !== false ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      </Box>

                      {configDialogError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{configDialogError}</Alert>
                      )}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                        <TextField
                          size="small"
                          label="Filter fields"
                          value={configSearchTerm}
                          onChange={(e) => setConfigSearchTerm(e.target.value)}
                          sx={{ minWidth: 220 }}
                          placeholder="Search by label..."
                        />
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>View</InputLabel>
                          <Select
                            value={configFilter}
                            label="View"
                            onChange={(e) => setConfigFilter(e.target.value)}
                          >
                            <MenuItem value="all">All fields</MenuItem>
                            <MenuItem value="required">Required only</MenuItem>
                            <MenuItem value="optional">Optional only</MenuItem>
                            <MenuItem value="configured">Configured only</MenuItem>
                            <MenuItem value="not_configured">Not configured</MenuItem>
                          </Select>
                        </FormControl>
                        {configHasUnsavedChanges && (
                          <Chip size="small" label="Unsaved changes" color="warning" variant="outlined" />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Status:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                          <Typography variant="caption" color="text.secondary">Configured</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                          <Typography variant="caption" color="text.secondary">Incomplete</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                          <Typography variant="caption" color="text.secondary">Required not configured</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400' }} />
                          <Typography variant="caption" color="text.secondary">Optional not configured</Typography>
                        </Box>
                      </Box>

                      {configDialogLoading ? (
                        <Box display="flex" alignItems="center" gap={1} sx={{ py: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading field configuration...</Typography>
                        </Box>
                      ) : (
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{
                            overflowX: 'auto',
                            scrollbarGutter: 'stable',
                            border: '1px solid #e0e0e0',
                            maxHeight: '60vh',
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Source Column Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 170, minWidth: 170 }}>Mapping Logic</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Value Configuration</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredConfigRows.map((row) => (
                                <TableRow
                                  key={row.field_key}
                                  sx={{
                                    bgcolor: (!row.is_required && !String(row.source_column_name || '').trim()) ? '#fafafa' : 'inherit',
                                  }}
                                >
                                  <TableCell sx={{ minWidth: 220 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Tooltip title={getRowStatus(row).label}>
                                        <Box
                                          sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor:
                                              getRowStatus(row).key === 'configured'
                                                ? 'success.main'
                                                : getRowStatus(row).key === 'incomplete'
                                                  ? 'warning.main'
                                                  : row.is_required
                                                    ? 'error.main'
                                                    : 'grey.400',
                                            flexShrink: 0,
                                          }}
                                        />
                                      </Tooltip>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {row.label}{row.is_required ? ' *' : ''}
                                      </Typography>
                                      {row.help_text && (
                                        // Hover mostra l'help; il click lo fissa aperto (utile anche su touch).
                                        <ClickAwayListener
                                          onClickAway={() => {
                                            if (pinnedHelpFieldKey === row.field_key) setPinnedHelpFieldKey(null);
                                          }}
                                        >
                                          <Box component="span" sx={{ display: 'inline-flex' }}>
                                            <Tooltip
                                              title={row.help_text}
                                              arrow
                                              placement="right"
                                              open={pinnedHelpFieldKey === row.field_key || hoveredHelpFieldKey === row.field_key}
                                              onOpen={() => setHoveredHelpFieldKey(row.field_key)}
                                              onClose={() => setHoveredHelpFieldKey(null)}
                                            >
                                              <IconButton
                                                size="small"
                                                aria-label={`Help for ${row.label}`}
                                                onClick={() =>
                                                  setPinnedHelpFieldKey((prev) => (prev === row.field_key ? null : row.field_key))
                                                }
                                                sx={{
                                                  p: 0.25,
                                                  color: pinnedHelpFieldKey === row.field_key ? 'primary.main' : 'text.secondary',
                                                  '&:hover': { color: 'primary.main' },
                                                }}
                                              >
                                                <HelpOutlineIcon sx={{ fontSize: 18 }} />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </ClickAwayListener>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={row.source_column_name}
                                      onChange={(e) => handleConfigRowChange(row.field_key, 'source_column_name', e.target.value)}
                                      fullWidth
                                      disabled={!savedIsMasterService}
                                      sx={{ minWidth: 200 }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: 170, minWidth: 170 }}>
                                    {row.allowed_logics.length === 1 ? (
                                      <TextField
                                        size="small"
                                        fullWidth
                                        value={mappingLogicOptions.find((opt) => opt.value === row.mapping_logic)?.label || row.mapping_logic}
                                        InputProps={{ readOnly: true }}
                                        disabled
                                        sx={
                                          row.is_required
                                            ? {
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                  WebkitTextFillColor: '#000',
                                                  color: '#000',
                                                },
                                              }
                                            : undefined
                                        }
                                      />
                                    ) : (
                                      <FormControl size="small" sx={{ minWidth: 150 }} fullWidth>
                                        <Select
                                          value={row.mapping_logic}
                                          disabled={!savedIsMasterService}
                                          onChange={(e) => {
                                            const nextLogic = e.target.value;
                                            handleConfigRowChange(row.field_key, 'mapping_logic', nextLogic);
                                            if (nextLogic === 'choice' || nextLogic === 'dimension') {
                                              ensureLookupOptions(getLookupDomainForRow({ ...row, mapping_logic: nextLogic }));
                                            }
                                          }}
                                          displayEmpty
                                          renderValue={(selected) => {
                                            if (!selected) {
                                              return <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>Select logic</span>;
                                            }
                                            return mappingLogicOptions.find((opt) => opt.value === selected)?.label || selected;
                                          }}
                                        >
                                          {mappingLogicOptions
                                            .filter((opt) => row.allowed_logics.length === 0 || row.allowed_logics.includes(opt.value))
                                            .map((opt) => (
                                              <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                              </MenuItem>
                                            ))}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {row.mapping_logic === 'mapping' ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => handleOpenMappingValuesDialog(row)}
                                        >
                                          {savedIsMasterService ? 'Mapping Values' : 'View Values'}
                                        </Button>
                                        {missingMappingAlertByField[row.field_key] && (
                                          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                                            Not configured
                                          </Typography>
                                        )}
                                      </Box>
                                    ) : row.mapping_logic === 'choice' && row.allow_multiple ? (
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          multiple
                                          value={row.single_value ? String(row.single_value).split(',').filter(Boolean) : []}
                                          displayEmpty
                                          disabled={!savedIsMasterService}
                                          onChange={(e) => {
                                            const next = Array.isArray(e.target.value)
                                              ? e.target.value
                                              : String(e.target.value).split(',');
                                            handleConfigRowChange(row.field_key, 'single_value', next.filter(Boolean).join(','));
                                          }}
                                          renderValue={(selected) => {
                                            if (!selected || selected.length === 0) {
                                              return <span style={{ color: '#9e9e9e' }}>{getSingleValuePlaceholder('choice')}</span>;
                                            }
                                            const options = lookupOptionsByDomain[getLookupDomainForRow(row)] || [];
                                            return selected
                                              .map((value) => {
                                                const option = options.find((x) => String(x.value) === String(value));
                                                return option ? formatLookupWithGroup(option) : value;
                                              })
                                              .join(', ');
                                          }}
                                        >
                                          {(lookupOptionsByDomain[getLookupDomainForRow(row)] || []).map((opt) => (
                                            <MenuItem key={`${row.field_key}-${opt.id ?? opt.value}`} value={opt.value}>
                                              {formatLookupWithGroup(opt)}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    ) : row.mapping_logic === 'choice' ? (
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={row.single_value || ''}
                                          displayEmpty
                                          disabled={!savedIsMasterService}
                                          onChange={(e) => handleConfigRowChange(row.field_key, 'single_value', e.target.value)}
                                          renderValue={(selected) => {
                                            if (!selected) return <span style={{ color: '#9e9e9e' }}>{getSingleValuePlaceholder('choice')}</span>;
                                            const option = (lookupOptionsByDomain[getLookupDomainForRow(row)] || []).find((x) => String(x.value) === String(selected));
                                            return option ? formatLookupWithGroup(option) : selected;
                                          }}
                                        >
                                          {(lookupOptionsByDomain[getLookupDomainForRow(row)] || []).map((opt) => (
                                            <MenuItem key={`${row.field_key}-${opt.id ?? opt.value}`} value={opt.value}>
                                              {formatLookupWithGroup(opt)}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    ) : row.mapping_logic === 'dimension' ? (
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={row.single_value || ''}
                                          displayEmpty
                                          disabled={!savedIsMasterService}
                                          onChange={(e) => handleConfigRowChange(row.field_key, 'single_value', e.target.value)}
                                          renderValue={(selected) => {
                                            if (!selected) return <span style={{ color: '#9e9e9e' }}>{getSingleValuePlaceholder('dimension')}</span>;
                                            const option = (lookupOptionsByDomain[getLookupDomainForRow(row)] || []).find((x) => String(x.value) === String(selected));
                                            return option?.label || selected;
                                          }}
                                        >
                                          {(lookupOptionsByDomain[getLookupDomainForRow(row)] || []).map((opt) => (
                                            <MenuItem key={`dimension-${opt.id ?? opt.value}`} value={opt.value}>
                                              {opt.label || opt.value}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {row.mapping_logic === 'format' && (
                                          <IconButton
                                            size="small"
                                            onClick={() => setFormatHelpOpen(true)}
                                            sx={{ border: '1px solid #d0d7de', borderRadius: '50%' }}
                                          >
                                            <HelpIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                        <TextField
                                          size="small"
                                          value={row.single_value || ''}
                                          onChange={(e) => handleConfigRowChange(row.field_key, 'single_value', e.target.value)}
                                          fullWidth
                                          placeholder={getSingleValuePlaceholder(row.mapping_logic)}
                                          disabled={!savedIsMasterService || row.mapping_logic === 'mapping' || row.mapping_logic === 'move'}
                                          sx={row.mapping_logic === 'format' ? { minWidth: 320 } : undefined}
                                        />
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {filteredConfigRows.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, fontStyle: 'italic' }}>
                                      No fields match the current filters.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <Box
                        sx={{
                          mt: 2,
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 2,
                          bgcolor: 'background.paper',
                          borderTop: '1px solid #e0e0e0',
                          pt: 1,
                        }}
                      >
                        {configDialogError && (
                          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setConfigDialogError(null)}>
                            {configDialogError}
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => handleSaveAllConfigRows({ finalize: false })}
                            disabled={configDialogSavingAll || configDialogLoading || !configHasUnsavedChanges}
                          >
                            {configDialogSavingAll ? 'Saving...' : 'Save Draft'}
                          </Button>
                          <Button
                            variant="contained"
                            color={selectedSheetMapping?.is_draft === false ? 'primary' : 'success'}
                            onClick={() => handleSaveAllConfigRows({ finalize: true })}
                            disabled={
                              configDialogSavingAll ||
                              configDialogLoading ||
                              (!configHasUnsavedChanges && selectedSheetMapping?.is_draft === false)
                            }
                          >
                            {configDialogSavingAll
                              ? 'Saving...'
                              : selectedSheetMapping?.is_draft === false
                              ? 'Save'
                              : 'Save & Finalize'}
                          </Button>
                          <Button onClick={handleCloseConfigDialog}>Close</Button>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                </Paper>
              )}
            </Box>
          </ProjectDetailTabPanel>
          )}

        </Card>
      </Box>

      {/* Dialog per modifica progetto */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth 
        scroll="body"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))',
            paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 48px))',
          },
        }}
        PaperProps={{
          sx: {
            margin: '0 auto',
            maxHeight: 'min(900px, calc(100vh - 144px))',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <EditNoteOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  Edit project
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Update settings for this project. Changes apply to worklog rules and ticket validation.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label="Editing" variant="outlined" sx={{ fontWeight: 500 }} />
                  {formData.active ? (
                    <Chip size="small" label="Active" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Inactive" variant="outlined" />
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              size="small"
              sx={{ color: 'text.secondary', mt: -0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5, bgcolor: 'grey.50', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack spacing={2.5}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)} sx={{ borderRadius: 1.5 }}>
                {formError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Core details
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Project name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    error={!formData.name.trim() && !!formError}
                    helperText={!formData.name.trim() && formError ? 'Name is required' : undefined}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="project_code"
                    label="Project code"
                    value={formData.project_code}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    error={!formData.project_code.trim() && !!formError}
                    helperText={!formData.project_code.trim() && formError ? 'Project code is required' : undefined}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required error={!formData.customer_id && !!formError}>
                    <InputLabel>Customer</InputLabel>
                    <Select
                      name="customer_id"
                      value={formData.customer_id}
                      onChange={handleFormChange}
                      label="Customer"
                      disabled={customersLoading}
                    >
                      {customersLoading ? (
                        <MenuItem value="">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CircularProgress size={20} />
                            <span>Loading customers…</span>
                          </Stack>
                        </MenuItem>
                      ) : (
                        customers.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                {ENABLE_TAB_CONTRACT && (<>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Service Type</InputLabel>
                    <Select
                      name="tipologia_servizio"
                      value={formData.tipologia_servizio}
                      onChange={handleFormChange}
                      label="Service Type"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {formData.tipologia_servizio && !typeOptions.some((o) => o.value === formData.tipologia_servizio) && (
                        <MenuItem value={formData.tipologia_servizio}>
                          {formData.tipologia_servizio} (inactive)
                        </MenuItem>
                      )}
                      {typeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label || opt.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!formData.customer_id}>
                    <InputLabel>Yubiq Code</InputLabel>
                    <Select
                      name="yubiq_code"
                      value={formData.yubiq_code}
                      onChange={handleFormChange}
                      label="Yubiq Code"
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {formData.yubiq_code && !yubiqOptions.includes(formData.yubiq_code) && (
                        <MenuItem value={formData.yubiq_code}>{formData.yubiq_code}</MenuItem>
                      )}
                      {yubiqOptions.map((code) => (
                        <MenuItem key={code} value={code}>{code}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                </>)}
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <ConfirmationNumberOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Ticket ID and validation
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  name="ticket_id_prefix"
                  label="Ticket ID prefix"
                  value={formData.ticket_id_prefix}
                  onChange={handleFormChange}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. SAAAMSABH-, JIRA-"
                  helperText="Optional. When set, this prefix is prepended for ticket resolution activities."
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <TextField
                    name="regex"
                    label="Regex pattern"
                    value={formData.regex}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g. ^[A-Z]+-\d+$"
                    helperText="Optional. Validates the part after the prefix, if a prefix is set."
                  />
                  <Tooltip title="Regex help and examples">
                    <IconButton
                      onClick={handleRegexHelpOpen}
                      color="primary"
                      sx={{
                        mt: { xs: 0, sm: 0.5 },
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    >
                      <HelpIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <TextField
                  name="regex_desc"
                  label="Regex description (shown to users)"
                  value={formData.regex_desc}
                  onChange={handleFormChange}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={2}
                  placeholder="Short hint for users when entering a ticket ID in worklogs"
                  helperText="Shown in worklog forms to explain the expected ticket format."
                />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Schedule
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="start_date"
                    label="Start date *"
                    type="date"
                    value={formData.start_date}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={!String(formData.start_date || '').trim() && !!formError}
                    helperText={
                      !String(formData.start_date || '').trim() && formError
                        ? 'Start date is required'
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="end_date"
                    label="End date *"
                    type="date"
                    value={formData.end_date}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={
                      (!String(formData.end_date || '').trim() && !!formError) ||
                      !!(
                        formData.start_date &&
                        formData.end_date &&
                        formData.end_date < formData.start_date
                      )
                    }
                    helperText={
                      formData.start_date &&
                      formData.end_date &&
                      formData.end_date < formData.start_date
                        ? 'Must be on or after start date'
                        : !String(formData.end_date || '').trim() && formError
                          ? 'End date is required'
                          : undefined
                    }
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TuneOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02 }}>
                  Worklog time window
                </Typography>
              </Stack>
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 1.5 }}>
                Control how many days before and after today users can add or edit worklogs for this project.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="days_before_block"
                    label="Days before (allowed)"
                    type="number"
                    value={formData.days_before_block}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    inputProps={{ min: 1, max: 365 }}
                    error={!formData.days_before_block || Number(formData.days_before_block) < 1}
                    helperText={
                      !formData.days_before_block || Number(formData.days_before_block) < 1
                        ? 'Must be greater than 0'
                        : 'Past window from today'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="days_after_block"
                    label="Days after (allowed)"
                    type="number"
                    value={formData.days_after_block}
                    onChange={handleFormChange}
                    required
                    fullWidth
                    variant="outlined"
                    inputProps={{ min: 1, max: 365 }}
                    error={!formData.days_after_block || Number(formData.days_after_block) < 1}
                    helperText={
                      !formData.days_after_block || Number(formData.days_after_block) < 1
                        ? 'Must be greater than 0'
                        : 'Future window from today'
                    }
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    name="previous_month_blocked"
                    checked={formData.previous_month_blocked}
                    onChange={handleFormChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                      Block previous month
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      When enabled, users cannot create or change worklogs for the previous calendar month.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.02, mb: 2 }}>
                Additional
              </Typography>
              <Stack spacing={2}>
                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  minRows={3}
                  fullWidth
                  variant="outlined"
                  placeholder="Internal notes or scope summary (optional)"
                />
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={formData.active}
                        onChange={handleFormChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Active project
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Inactive projects are hidden from most operational flows.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                </Paper>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={handleCloseDialog} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveProject}
            variant="contained"
            color="primary"
            disabled={saving}
            sx={{ minWidth: 140 }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save changes'}
          </Button>
        </DialogActions>
        </Dialog>

        <Dialog
          open={mappingValuesDialogOpen}
          onClose={() => setMappingValuesDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Mapping Values
            {selectedConfigRowForValues ? ` - ${selectedConfigRowForValues.label}` : ''}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {mappingValuesError && <Alert severity="error">{mappingValuesError}</Alert>}
              {mappingValuesLoading ? (
                <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading mapping values...</Typography>
                </Box>
              ) : (
                <>
                  {savedIsMasterService && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Normalized Value</InputLabel>
                        <Select
                          value={mappingValueForm.normalized_ref_id}
                          label="Normalized Value"
                          onChange={(e) => setMappingValueForm((prev) => ({ ...prev, normalized_ref_id: e.target.value }))}
                        >
                          {normalizedOptionsForValues.map((opt) => (
                            <MenuItem key={opt.id} value={String(opt.id)}>
                              {formatLookupWithGroup(opt)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Source Value"
                        value={mappingValueForm.source_value}
                        onChange={(e) => setMappingValueForm((prev) => ({ ...prev, source_value: e.target.value }))}
                        sx={{ minWidth: 220 }}
                      />
                      <Button variant="contained" onClick={handleSaveMappingValue}>
                        {mappingValueForm.id ? 'Update' : 'Add'}
                      </Button>
                      {mappingValueForm.id && (
                        <Button
                          variant="outlined"
                          onClick={() => setMappingValueForm({ id: null, source_value: '', normalized_ref_id: '' })}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Normalized Value</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Source Value</TableCell>
                          {savedIsMasterService && <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappingValues.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={savedIsMasterService ? 3 : 2} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, fontStyle: 'italic' }}>
                                No mapping values yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          mappingValues.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {(() => {
                                  const match = normalizedOptionsForValues.find((x) => x.id === item.normalized_ref_id);
                                  return match ? formatLookupWithGroup(match) : item.normalized_ref_id;
                                })()}
                              </TableCell>
                              <TableCell>{item.source_value}</TableCell>
                              {savedIsMasterService && (
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="outlined" onClick={() => handleEditMappingValue(item)}>
                                      Edit
                                    </Button>
                                    <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteMappingValue(item.id)}>
                                      Delete
                                    </Button>
                                  </Box>
                                </TableCell>
                              )}
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMappingValuesDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={formatHelpOpen}
          onClose={() => setFormatHelpOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Format Help</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please insert the data format as appears in gsheet, checking the real value of the cell by clicking on it.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Write the pattern following this naming convention
              </Typography>
              <Typography variant="body2">YYYY: Years (es. 2026).</Typography>
              <Typography variant="body2">MM: Month (01-12).</Typography>
              <Typography variant="body2">DD: Days (01-31).</Typography>
              <Typography variant="body2">HH: hours 24h (00-23).</Typography>
              <Typography variant="body2">MM: Minutes (00-59).</Typography>
              <Typography variant="body2">SS: Seconds (00-59).</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>XXX:decimals</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Example</Typography>
              <Typography variant="body2">YYYY-MM-DD HH:MM:SS.XXX</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormatHelpOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Regex Help Dialog */}
        <Dialog 
          open={regexHelpOpen} 
          onClose={handleRegexHelpClose}
          maxWidth="md"
          fullWidth
          scroll="body"
          sx={{
            '& .MuiDialog-container': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              paddingLeft: '16px',
              paddingRight: '16px',
              paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))',
              paddingTop: 'max(72px, calc(env(safe-area-inset-top, 0px) + 48px))',
            },
          }}
          PaperProps={{
            sx: {
              margin: '0 auto',
              maxHeight: 'min(900px, calc(100vh - 144px))',
              width: '100%',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpIcon color="primary" />
              <Typography variant="h6">Regex Pattern Help</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to fill in the Ticket ID regex field
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              A <strong>regex</strong> helps check that your Ticket IDs follow the correct pattern. If you don’t know how to write one, just use one of the examples below or change the letters/numbers to fit your needs.
            </Typography>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Ready-to-use examples:
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Ticket Format</Box>
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Use this regex</Box>
                </Box>
              </Box>
              <Box component="tbody">
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>ABC-123 (Jira)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Z]+-\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>12345 (Numbers only)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>INC0012345 (ServiceNow)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^INC\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>REQ45A (Letters+Numbers)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Za-z0-9]+$</code></Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to set exact or maximum length limits
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>
                <Typography variant="body2"><strong>To require exactly 5 numbers</strong>: Use <code>^\d{'{'}5{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require between 3 and 6 letters</strong>: Use <code>^[A-Za-z]{'{'}3,6{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>ABC</code>, <code>abcd</code>, <code>AbCdEf</code>. Not valid: <code>AB</code>, <code>ABCDEFG</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at most 4 digits</strong>: Use <code>^\d{'{'}1,4{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>1</code>, <code>1234</code>. Not valid: <code>12345</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at least 3 digits</strong>: Use <code>^\d{'{'}3,{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>123</code>, <code>1234567</code></Typography>
              </li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              What do the main symbols mean?
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li><Typography variant="body2"><code>^</code> → means “must start with”</Typography></li>
              <li><Typography variant="body2"><code>$</code> → means “must end with”</Typography></li>
              <li><Typography variant="body2"><code>[A-Z]</code> → one UPPERCASE letter</Typography></li>
              <li><Typography variant="body2"><code>[a-z]</code> → one lowercase letter</Typography></li>
              <li><Typography variant="body2"><code>\d</code> → a digit (0 to 9)</Typography></li>
              <li><Typography variant="body2"><code>+</code> → one or more of the previous character</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n{'}'}</code> → exactly n characters</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,m{'}'}</code> → between n and m characters (inclusive)</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,{'}'}</code> → at least n characters</Typography></li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Practical examples
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 1 }}>
              <li>
                <Typography variant="body2"><strong>Exactly 5 numbers ("12345")</strong>: Use <code>^\d{5}$</code></Typography>
                <Typography variant="body2">Valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Numbers only, any length ("12345")</strong>: Use <code>^\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>1987</code>, <code>00234</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Jira ticket ("ABC-123")</strong>: Use <code>^[A-Z]+-\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>DEF-45</code>. Not valid: <code>def-45</code>, <code>DEF_45</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>ServiceNow ticket ("INC0012345")</strong>: Use <code>^INC\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>INC123</code>. Not valid: <code>INC-123</code>, <code>INCabc</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Letters and numbers mixed ("REQ45A")</strong>: Use <code>^[A-Za-z0-9]+$</code></Typography>
                <Typography variant="body2">Valid: <code>AB123C</code>. Not valid: <code>REQ 123</code>, <code>REQ-123</code></Typography>
              </li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Test your regex patterns using online tools like regex101.com or regexr.com before implementing them in your project.
                If you need more help, feel free to open a ticket.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRegexHelpClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={mappingDialogOpen}
          onClose={handleCloseMappingDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingSheetMappingId
              ? (mappingDialogType === 'field' ? 'Edit Field Mapping' : 'Edit SLA Mapping')
              : (mappingDialogType === 'field' ? 'Add Field Mapping' : 'Add SLA Mapping')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {mappingDialogError && (
                <Alert severity="error">{mappingDialogError}</Alert>
              )}
              <TextField
                name="file_url"
                label="File URL"
                value={mappingDialogForm.file_url}
                onChange={handleMappingDialogChange}
                required
                fullWidth
                size="small"
                placeholder="https://docs.google.com/spreadsheets/..."
              />
              <TextField
                name="file_name"
                label="File Name"
                value={mappingDialogForm.file_name}
                onChange={handleMappingDialogChange}
                required
                fullWidth
                size="small"
                placeholder="e.g. Incident_Q2_2026"
              />
              {mappingDialogType === 'sla' ? (
                <FormControl fullWidth size="small" required disabled={!!editingSheetMappingId}>
                  <InputLabel>Data Structure</InputLabel>
                  <Select
                    name="data_structure"
                    value={mappingDialogForm.data_structure}
                    label="Data Structure"
                    onChange={handleMappingDialogChange}
                  >
                    {dataStructureOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!editingSheetMappingId && (
                    <FormHelperText>Data Structure cannot be changed after creation.</FormHelperText>
                  )}
                </FormControl>
              ) : (
                <FormControl fullWidth size="small" required>
                  <InputLabel>Ticketing System</InputLabel>
                  <Select
                    name="ticketing_system"
                    value={mappingDialogForm.ticketing_system}
                    label="Ticketing System"
                    onChange={handleMappingDialogChange}
                  >
                    {ticketingOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMappingDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveMappingDialog} disabled={mappingSaving}>
              {mappingSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{confirmDialogTitle || 'Confirm action'}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{confirmDialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose} disabled={confirmDialogBusy}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmDialogConfirm} disabled={confirmDialogBusy}>
              {confirmDialogBusy ? 'Please wait...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={mappingToast.open}
          autoHideDuration={3000}
          onClose={() => setMappingToast((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setMappingToast((prev) => ({ ...prev, open: false }))}
            severity={mappingToast.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {mappingToast.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }


